/**
 * Iter 67bl — Vendor "Rates" tab (date-range pricing across the whole fleet).
 *
 * Replaces the old vendor-wide Seasonal pricing tab (4 multipliers × car-type
 * matrix) with a per-car date-range rates dashboard. Each car shows its base
 * price + a collapsible block of its rate rules (re-using the same
 * CarRateRulesEditor that lives inside the car edit form, so behaviour stays
 * identical between the two entry points).
 *
 * Vendors with thin fleets see everything expanded; vendors with 20+ cars
 * can collapse rows by default.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp, Loader2, CalendarRange, Tag } from "lucide-react";
import { toast } from "sonner";
import CarRateRulesEditor from "@/components/vendor/CarRateRulesEditor";
import SeasonalPricing from "@/components/SeasonalPricing";
import Money from "@/components/Money";

function CarRatesRow({ car, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const [ruleCount, setRuleCount] = useState(null);

  // Lightweight count fetch so the header chip can show "3 rules" without
  // mounting the full editor. Skipped when the row is already opened (the
  // editor will load its own list anyway).
  useEffect(() => {
    if (open || ruleCount != null) return undefined;
    let alive = true;
    api.get(`/vendor/cars/${car.id}/rate-rules`)
      .then((r) => { if (alive) setRuleCount(r.data.count || 0); })
      .catch(() => { if (alive) setRuleCount(0); });
    return () => { alive = false; };
  }, [car.id, open, ruleCount]);

  return (
    <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden" data-testid={`rates-row-${car.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-neutral-50/60 transition"
        data-testid={`rates-row-toggle-${car.id}`}
      >
        {/* Car thumbnail */}
        {car.images?.[0] ? (
          <img src={car.images[0]} alt={car.make} className="h-14 w-20 rounded-lg object-cover ring-1 ring-neutral-200 flex-shrink-0" />
        ) : (
          <div className="h-14 w-20 rounded-lg bg-neutral-100 flex items-center justify-center text-neutral-300 flex-shrink-0">
            <Tag className="h-5 w-5" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-neutral-900 truncate text-base">{car.make} {car.model}</div>
          <div className="text-xs text-neutral-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span>{car.year}</span>
            {car.plate_number && <><span>·</span><span className="tabular-nums">{car.plate_number}</span></>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" data-testid={`rates-base-price-${car.id}`}>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 ring-1 ring-emerald-200 text-emerald-800">
            <span className="text-[9px] uppercase tracking-wider font-bold opacity-70">Base</span>
            <Money amount={car.price_per_day} className="font-bold text-sm tabular-nums" />
            <span className="text-[10px] font-semibold opacity-70">/day</span>
          </span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {ruleCount != null && (
            <Badge variant="outline" className={ruleCount > 0 ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-neutral-50 text-neutral-500 border-neutral-200"}>
              <CalendarRange className="h-3 w-3 mr-1" /> {ruleCount} {ruleCount === 1 ? "rule" : "rules"}
            </Badge>
          )}
          {open ? <ChevronUp className="h-4 w-4 text-neutral-400" /> : <ChevronDown className="h-4 w-4 text-neutral-400" />}
        </div>
      </button>
      {open && (
        <div className="border-t border-border bg-neutral-50/40 px-5 py-4">
          {/* Iter 67bn — context strip inside the expanded editor: bigger
              photo + make/model/year/plate so the vendor never forgets which
              car they're editing rules for. */}
          <div className="flex items-center gap-4 pb-4 mb-4 border-b border-border" data-testid={`rates-expanded-context-${car.id}`}>
            {car.images?.[0] ? (
              <img src={car.images[0]} alt={`${car.make} ${car.model}`} className="h-20 w-32 rounded-xl object-cover ring-1 ring-neutral-200 shadow-sm flex-shrink-0" />
            ) : (
              <div className="h-20 w-32 rounded-xl bg-neutral-100 flex items-center justify-center text-neutral-300 flex-shrink-0">
                <Tag className="h-7 w-7" />
              </div>
            )}
            <div className="min-w-0">
              <div className="font-display text-lg font-semibold text-neutral-900 truncate">{car.make} {car.model}</div>
              <div className="text-xs text-neutral-500 mt-1 flex items-center gap-2 flex-wrap">
                <span>{car.year}</span>
                {car.plate_number && <><span>·</span><span className="tabular-nums">{car.plate_number}</span></>}
                {car.car_type && <><span>·</span><span className="capitalize">{car.car_type}</span></>}
              </div>
            </div>
          </div>
          <CarRateRulesEditor carId={car.id} basePricePerDay={car.price_per_day} />
        </div>
      )}
    </div>
  );
}

export default function VendorRatesTab() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/cars/mine")
      .then((r) => {
        if (!alive) return;
        const list = Array.isArray(r.data) ? r.data : (r.data?.cars || []);
        setCars(list);
      })
      .catch((err) => toast.error(formatApiError(err) || "Could not load your cars."))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Iter 67bp — per-car rows now stay collapsed by default regardless of
  // fleet size. Vendors click the car header to reveal its rule editor. This
  // turns the second section into a clean fleet directory instead of a tall
  // wall of editors.

  return (
    <div className="space-y-6" data-testid="vendor-rates-tab">
      {/* SECTION 1 (first) — Default rates by season per car type */}
      {!loading && cars.length > 0 && (
        <div data-testid="vendor-seasonal-section">
          <div className="rounded-2xl border border-amber-100 bg-gradient-to-br from-amber-50/40 to-white p-5 mb-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white shadow-md shadow-amber-200">
                <CalendarRange className="h-4 w-4" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Defaults · Seasonal multipliers</div>
                <div className="font-semibold text-neutral-900 text-sm">Default rates by season per car type</div>
              </div>
            </div>
            <p className="text-xs text-neutral-600 mt-2 leading-relaxed">
              These multiply the car&apos;s base price on every booking <strong>unless a per-car date-range rule below covers the day</strong>. Use <code className="text-[11px] bg-white/80 px-1 rounded">1.0</code> for no change, or e.g. <code className="text-[11px] bg-white/80 px-1 rounded">1.30</code> to add 30% in summer.
            </p>
          </div>
          <SeasonalPricing vendorCars={cars} />
        </div>
      )}

      {/* SECTION 2 (second) — Per-car date-range rates, collapsed by default */}
      <div className="rounded-2xl border border-violet-100 bg-gradient-to-br from-violet-50/40 to-white p-5">
        <div className="flex items-center gap-3 mb-1">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center text-white shadow-md shadow-violet-200">
            <CalendarRange className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700">Override · Date-range rates</div>
            <div className="font-semibold text-neutral-900 text-sm">Per-car custom pricing windows</div>
          </div>
        </div>
        <p className="text-xs text-neutral-600 mt-2 leading-relaxed">
          Click a vehicle below to add or edit its custom <strong>€/day</strong> windows (e.g. <em>Christmas peak €180</em>, <em>August bank holiday €120</em>). The shortest matching range wins at quote time — days no rule covers fall back to the seasonal-multiplier defaults above.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12 text-neutral-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading your fleet…</div>
      ) : cars.length === 0 ? (
        <div className="bg-white rounded-2xl border border-border p-10 text-center text-neutral-500">
          You don&apos;t have any vehicles yet. Add a car from the My cars tab to start setting rate rules.
        </div>
      ) : (
        <div className="space-y-3">
          {cars.map((c) => <CarRatesRow key={c.id} car={c} defaultOpen={false} />)}
        </div>
      )}
    </div>
  );
}
